import { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../api/core';

const DEBOUNCE_MS = 600;

export interface BlogPostPreviewData {
  title: string;
  content: string;
  excerpt: string;
  featuredImageUrl: string;
  featuredImageCrop?: {
    x: number;
    y: number;
    zoom: number;
    aspect: number;
    croppedAreaPixels?: { x: number; y: number; width: number; height: number };
    croppedAreaPercent?: { x: number; y: number; width: number; height: number };
    grayscale?: boolean;
  };
  authorName: string;
  tags: string[];
  readingTimeMinutes: number;
}

/**
 * Fetches server-rendered HTML for a draft blog post via POST and returns
 * the raw HTML string for use with iframe srcdoc.
 */
export function useBlogPostPreview(
  siteId: string | undefined,
  postData: BlogPostPreviewData | undefined,
  isOpen: boolean
) {
  const [html, setHtml] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastHashRef = useRef<string>('');

  const fetchPreview = useCallback(
    async (data: BlogPostPreviewData) => {
      if (!siteId) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('authToken');

        const res = await fetch(`${API_BASE_URL}/sites/${siteId}/apps/blog/posts/preview-html`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(data),
          signal: controller.signal,
        });

        if (!res.ok) {
          throw new Error(`Preview failed (${res.status})`);
        }

        const text = await res.text();
        setHtml(text);
      } catch (err: any) {
        if (err.name === 'AbortError') return;
        console.error('Blog preview fetch error:', err);
        setError(err.message || 'Failed to load preview');
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [siteId]
  );

  useEffect(() => {
    if (!isOpen || !postData || !siteId) return;

    const hash = JSON.stringify(postData);
    if (hash === lastHashRef.current && html) return;
    lastHashRef.current = hash;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      fetchPreview(postData);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // html intentionally excluded to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postData, isOpen, siteId, fetchPreview]);

  useEffect(() => {
    if (!isOpen) {
      abortRef.current?.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setHtml(null);
      lastHashRef.current = '';
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const refresh = useCallback(() => {
    if (postData) {
      lastHashRef.current = '';
      fetchPreview(postData);
    }
  }, [postData, fetchPreview]);

  return { html, isLoading, error, refresh };
}
